import { TextField, type TextFieldProps } from '@mui/material';
import {
  type FieldValues,
  type RegisterOptions,
  useController,
  useFormContext,
} from 'react-hook-form';

type BaseFieldProps = {
  name: string;
  rules?: RegisterOptions;
} & Omit<TextFieldProps, 'name' | 'value' | 'onChange' | 'onBlur'>;

const isInvalidDate = (date: Date) => Number.isNaN(date.getTime());

export const BaseField = ({ name, rules, defaultValue = '', ...props }: BaseFieldProps) => {
  const { control } = useFormContext<FieldValues>();
  const { field, formState } = useController({
    name,
    control,
    rules,
  });
  const errorMessage = formState.errors?.[name]?.message
    ? String(formState.errors[name].message)
    : undefined;

  let value = field.value;
  let onChange = field.onChange;

  if (props.type === 'date' || props.type === 'datetime-local') {
    if (value) {
      let d = new Date(value);

      // <input type="datetime-local"> はISO文字列の一部しか受け付けずタイムゾーンが考慮されないため
      // タイムゾーンの影響を打ち消して文字列が正しくJSTになるようにする
      d = new Date(Number(d) - d.getTimezoneOffset() * 60000);
      if (props.type === 'date') {
        value = d.toISOString().split('T')[0]; // YYYY-MM-DD
      } else {
        value = d.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
      }
    }

    onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const date = new Date(e.target.value);

      // 日付をクリアすると空文字になる
      if (isInvalidDate(date)) {
        field.onChange(null);
      } else {
        if (props.type === 'date') {
          // UTCのYYYY-MM-DD 00:00として解釈されるため、JSTのYYYY-MM-DD 00:00になるようにする
          const jstDate = new Date(Number(date) + date.getTimezoneOffset() * 60000);
          field.onChange(jstDate.toISOString());
        } else {
          field.onChange(date.toISOString());
        }
      }
    };
  } else if (props.type === 'number') {
    onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const num = e.target.value !== '' ? Number(e.target.value) : undefined;
      field.onChange(num);
    };
  }

  return (
    <TextField
      variant="outlined"
      {...props}
      inputRef={field.ref}
      name={field.name}
      value={value ?? defaultValue}
      onChange={onChange}
      onBlur={field.onBlur}
      error={!!errorMessage}
      helperText={errorMessage}
      slotProps={{
        inputLabel: { shrink: true },
      }}
    />
  );
};
